import { describeRequest, formatPhoneForDisplay, REGISTRANT_TYPE_LABELS } from '@crew/shared';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdmin } from '@/auth/AuthProvider';
import type { RequestView } from '@/hooks/useAdminData';
import { formatWhen } from '@/lib/activity';
import { formatJobDate } from '@/lib/format';
import { tsToDate } from '@/lib/ts';
import { adminApi } from '@/services/adminApi';
import { errorMessage } from '@/services/apiError';
import { PhoneIcon } from '../icons';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';

/**
 * כרטיס בקשת שינוי. הכפתורים שולחים לשרת, שמאשר/דוחה באופן אטומי.
 * אם מנהל אחר כבר טיפל — מוצגת הודעה עם שמו, והכרטיס נעלם מהרשימה (הרשימה חיה).
 */
export function RequestCard({ r, showJob = true }: { r: RequestView; showJob?: boolean }) {
  const { user } = useAdmin();
  const toast = useToast();
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const created = tsToDate(r.createdAt);
  const handled = tsToDate(r.handledAt);
  const cancel = r.requestedCount === 0;
  const pending = r.status === 'pending';

  async function decide(decision: 'approve' | 'reject') {
    setBusy(decision);
    try {
      const res = await adminApi(user).decide(r.id, decision);
      if (res.status === 'cancelled') toast('הרישום כבר הוסר — הבקשה בוטלה');
      else toast(decision === 'approve' ? 'הבקשה אושרה' : 'הבקשה נדחתה');
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  }

  return (
    <article className={`rounded-card border bg-surface p-4 ${pending ? 'border-line' : 'border-line opacity-90'}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded-[5px] px-1.5 py-0.5 text-[11px] font-semibold ${r.registrantType === 'contractor' ? 'bg-brand text-white' : 'bg-sunken text-ink-2'}`}
            >
              {REGISTRANT_TYPE_LABELS[r.registrantType]}
            </span>
            <h3 className="truncate text-[16px] font-bold">{r.contractorName}</h3>
          </div>
          <a href={`tel:${r.contractorPhone}`} className="ltr mt-0.5 inline-flex items-center gap-1 text-[13px] text-ink-2 hover:text-brand">
            <PhoneIcon size={13} />
            {formatPhoneForDisplay(r.contractorPhone)}
          </a>
          {showJob && (
            <Link to={`/jobs/${r.jobId}`} className="mt-1 block truncate text-[13px] text-brand hover:underline">
              {r.jobName} · {formatJobDate(r.jobDate)}
            </Link>
          )}
        </div>
        {/* מ- → ל- */}
        <div className="tabular flex shrink-0 items-center gap-1.5" aria-label={describeRequest(r.previousCount, r.requestedCount)}>
          <span className="rounded-[6px] bg-sunken px-2 py-1 text-[16px] font-bold text-ink-2 line-through decoration-ink-3">{r.previousCount}</span>
          <span className="text-ink-3" aria-hidden="true">
            ←
          </span>
          <span
            className={`rounded-[6px] px-2 py-1 text-[16px] font-extrabold ${cancel ? 'bg-danger text-white' : 'bg-tag text-tag-ink shadow-[inset_0_-3px_0_var(--color-tag-bar)]'}`}
          >
            {cancel ? 'ביטול' : r.requestedCount}
          </span>
        </div>
      </div>

      {r.note && <p className="mt-2 rounded-[8px] bg-paper px-3 py-2 text-[14px] text-ink-2">"{r.note}"</p>}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[12px] text-ink-3">{created ? formatWhen(created) : ''}</span>
        {pending ? (
          <div className="flex gap-2">
            <Button variant="secondary" className="min-h-10 px-4 text-[14px]" loading={busy === 'reject'} disabled={busy !== null} onClick={() => decide('reject')}>
              דחייה
            </Button>
            <Button className="min-h-10 px-4 text-[14px]" loading={busy === 'approve'} disabled={busy !== null} onClick={() => decide('approve')}>
              {cancel ? 'אישור ביטול' : 'אישור'}
            </Button>
          </div>
        ) : (
          <span className="text-[13px] text-ink-2">
            {r.status === 'approved' ? 'אושרה' : r.status === 'rejected' ? 'נדחתה' : 'בוטלה'}
            {r.handledByName ? ` · ${r.handledByName}` : ''}
            {handled ? ` · ${formatWhen(handled)}` : ''}
          </span>
        )}
      </div>
    </article>
  );
}
